import React from 'react';
import { FileText, Plus, ArrowsClockwise, StackSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { CardContent } from '@/components/ui/card';
import type { QuestionItem } from './types';
import type { DatabaseFileLabel } from 'shared/components/UniversalPickerModal';

interface DocumentLinkSectionProps {
  question: QuestionItem;
  questionIndex: number;
  fileLabels: DatabaseFileLabel[];
  onUpdate: (index: number, updates: Partial<QuestionItem>) => void;
  onOpenDocumentLabelPicker: (questionIndex: number) => void;
}

export const DocumentLinkSection: React.FC<DocumentLinkSectionProps> = ({
  question,
  questionIndex,
  fileLabels,
  onUpdate,
  onOpenDocumentLabelPicker,
}) => {
  return (
    <CardContent className="pt-4 border-t border-dashed" data-tour="dd-file-label-link">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-600" weight="duotone" />
          <span className="text-sm font-medium text-blue-700">Koppel aan documenten</span>
          <span className="text-xs text-muted-foreground">(optioneel - voor automatische evaluatie)</span>
        </div>
        {question.documentLabelIds.length === 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onOpenDocumentLabelPicker(questionIndex)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Document label toevoegen
          </Button>
        )}
      </div>

      {question.documentLabelIds.length > 0 && (
        <div className="mt-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
          {/* Linked Document Labels */}
          <div className="space-y-2">
            <Label className="text-xs text-blue-700">Gelinkte document labels</Label>
            <div className="flex flex-wrap gap-2">
              {question.documentLabelIds.map((labelId) => {
                const label = fileLabels.find((l) => l.id === labelId);
                return (
                  <Badge key={labelId} variant="secondary" className="flex items-center gap-1 pr-1 bg-white">
                    <FileText className="h-3 w-3 text-blue-600" />
                    {label?.label || `Label ${labelId}`}
                    <button
                      type="button"
                      className="ml-1 h-4 w-4 rounded-full hover:bg-red-100 flex items-center justify-center text-muted-foreground hover:text-red-600"
                      onClick={() =>
                        onUpdate(questionIndex, {
                          documentLabelIds: question.documentLabelIds.filter((id) => id !== labelId),
                        })
                      }
                    >
                      ×
                    </button>
                  </Badge>
                );
              })}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 text-xs"
                onClick={() => onOpenDocumentLabelPicker(questionIndex)}
              >
                <Plus className="h-3 w-3 mr-1" />
                Label
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Configureer voorwaarden per antwoordoptie hierboven om automatische evaluatie in te stellen. Het systeem
              itereert over alle bestanden met deze labels en controleert de voorwaarden.
            </p>
          </div>

          {/* Document behavior toggle */}
          <div className="mt-4 pt-4 border-t border-blue-100 space-y-3">
            <Label className="text-xs text-blue-700">Document gedrag</Label>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className={`flex items-start gap-3 p-2.5 rounded-md border text-left transition-colors ${
                  question.documentBehavior !== 'cumulative'
                    ? 'border-sky-500 bg-sky-50'
                    : 'border-neutral-200 hover:bg-neutral-50'
                }`}
                onClick={() => onUpdate(questionIndex, { documentBehavior: 'replacing', cumulativeConfig: null })}
              >
                <ArrowsClockwise className="h-4 w-4 mt-0.5 text-sky-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Vervangend</p>
                  <p className="text-xs text-muted-foreground">
                    Alleen het meest recente document bepaalt het antwoord.
                  </p>
                </div>
              </button>

              <button
                type="button"
                className={`flex items-start gap-3 p-2.5 rounded-md border text-left transition-colors ${
                  question.documentBehavior === 'cumulative'
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-neutral-200 hover:bg-neutral-50'
                }`}
                onClick={() =>
                  onUpdate(questionIndex, {
                    documentBehavior: 'cumulative',
                    cumulativeConfig: {
                      approvalStatuses: ['verleend', 'goedgekeurd', 'vergund'],
                      trackAllDocuments: true,
                      enableSynthesis: true,
                    },
                  })
                }
              >
                <StackSimple className="h-4 w-4 mt-0.5 text-violet-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Cumulatief</p>
                  <p className="text-xs text-muted-foreground">
                    Alle documenten worden gecombineerd. Toont tijdlijn.
                  </p>
                </div>
              </button>
            </div>

            {question.documentBehavior === 'cumulative' && (
              <div className="mt-3 pt-3 border-t border-violet-200 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">AI samenvatting genereren</Label>
                  <Switch
                    checked={question.cumulativeConfig?.enableSynthesis ?? true}
                    onCheckedChange={(checked) =>
                      onUpdate(questionIndex, {
                        cumulativeConfig: { ...question.cumulativeConfig, enableSynthesis: checked },
                      })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Alle documenten tracken</Label>
                  <Switch
                    checked={question.cumulativeConfig?.trackAllDocuments ?? true}
                    onCheckedChange={(checked) =>
                      onUpdate(questionIndex, {
                        cumulativeConfig: { ...question.cumulativeConfig, trackAllDocuments: checked },
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </CardContent>
  );
};
